import { createContext, type MiddlewareFunction } from "react-router";
import { translate } from "../i18n/index.js";
import { groupWorkspace } from "./analytics.js";
import { authClient } from "./auth-client.js";
import { ApiRequestError, type WorkspaceSummary } from "./api.js";
import { apiContext, refreshSession, sessionContext } from "./session.js";

// Workspace scope is carried by the URL (/w/:workspaceSlug). Every API call for
// a page takes its workspace id from this context, so switching workspaces
// changes the API context structurally, never through hidden client state.
export const workspaceContext = createContext<WorkspaceSummary>();

const LAST_WORKSPACE_KEY = "edm.lastWorkspaceSlug";

export function rememberWorkspace(slug: string) {
  try {
    localStorage.setItem(LAST_WORKSPACE_KEY, slug);
  } catch {
    // storage may be unavailable (privacy mode); the URL is the source of truth anyway
  }
}

export function lastWorkspaceSlug(): string | null {
  try {
    return localStorage.getItem(LAST_WORKSPACE_KEY);
  } catch {
    return null;
  }
}

const bySlug = (me: { workspaces: WorkspaceSummary[] } | null, slug: string | undefined) => me?.workspaces.find((w) => w.slug === slug);

export const workspaceMiddleware: MiddlewareFunction = async ({ params, context }, next) => {
  let workspace = bySlug(context.get(sessionContext), params.workspaceSlug);
  if (!workspace) {
    // The session is served from cache, so a workspace created or joined in
    // this tab may not be in it yet. Re-read once before calling the slug unknown.
    const me = await refreshSession(context.get(apiContext));
    if (me) context.set(sessionContext, me);
    workspace = bySlug(me, params.workspaceSlug);
  }
  if (!workspace) {
    throw new ApiRequestError(404, { code: "not_found", message: translate("notFound.workspace") });
  }
  context.set(workspaceContext, workspace);
  groupWorkspace(workspace);
  if (lastWorkspaceSlug() !== workspace.slug) {
    rememberWorkspace(workspace.slug);
    // Keep Better Auth's active organization aligned for features that rely on it; not used for authorization.
    void authClient.organization.setActive({ organizationId: workspace.id }).catch(() => undefined);
  }
  return next();
};
